import type { ClassifiedResult } from "./whatif-noise.js";
import { scrubIdentifiers } from "./identifier-patterns.js";

/**
 * Value-free renderings of a classified what-if result.
 *
 * The default renderer prints `before → after`, which for a live deployment means
 * printing live configuration values. Anywhere the output is not private — a public
 * build log, an automated report — it must go through here instead.
 *
 * Two tiers, deliberately different:
 *   - `redactForLog`    keeps enough to diagnose (type, name, path, reason), no values.
 *   - `redactForReport` keeps counts only, for copy that is permanent and public.
 */
export function redactForLog(r: ClassifiedResult): string {
  const lines: string[] = [];
  const section = (title: string, items: ClassifiedResult["unexpected"]): void => {
    if (items.length === 0) return;
    lines.push(`${title} (${items.length.toString()}):`);
    for (const c of items) {
      const reason = c.reason ? `   [${c.reason}]` : "";
      lines.push(`  • ${c.resourceType} ${c.resourceName} ${c.path}${reason}`);
    }
  };
  section("Unexpected", r.unexpected);
  section("Known pending", r.pending);
  lines.push(`Noise suppressed: ${r.noise.length.toString()}`);
  return lines.join("\n");
}

export function redactForReport(r: ClassifiedResult): { unexpected: number; pending: number; noise: number } {
  return { unexpected: r.unexpected.length, pending: r.pending.length, noise: r.noise.length };
}

/**
 * Redact a comparison-RUN FAILURE for a public surface — as opposed to
 * `redactForLog`/`redactForReport` above, which redact a comparison that ran
 * to completion. This governs the other path: the comparison threw before it
 * could produce a result at all.
 *
 * `runAz`'s failure message (see ./az.ts's AZ_COMMAND_FAILED) embeds the
 * ENTIRE failed command line ahead of `' failed: <the tool's own error
 * text>'` — including every `--parameters` value: the tenant id, the client
 * id, and every ARM resource id passed in (each of which also carries the
 * subscription id as a path segment). Reproducing that verbatim in a public
 * CI log defeats --redact on exactly the path most likely to be unattended
 * when it fires. This drops the reconstructed command line entirely and
 * keeps only the underlying tool's own trailing explanation, itself scrubbed
 * of any identifier-shaped value as defense in depth (some Azure CLI errors
 * echo back a resource id too).
 */
export function redactWhatIfFailure(message: string): string {
  const commandLineFailure = /^'.*?' failed: ([\s\S]*)$/.exec(message);
  const tail = commandLineFailure ? commandLineFailure[1] : message;
  return `the underlying comparison command failed (command line withheld): ${scrubIdentifiers(tail)}`;
}
